import {
  CUSTOM_TRACK_PREVIEW_KEY,
  CUSTOM_TRACK_SCHEMA_VERSION,
  normalizeCustomTrackDefinition,
  normalizeCustomTrackId,
  normalizeCustomTrackPreviewDocument,
  normalizeCustomTrackSavedDocument,
  type CustomTrackDefinition,
  type CustomTrackPoint,
} from "../game/simulation/customTrackSchema";
import { buildCustomTrackLayout, CUSTOM_TRACK_METRES_PER_UNIT, type CustomTrackLayout } from "../game/simulation/customTrackLayout";
import { CUSTOM_TRACK_THEME_KEYS, getCustomTrackTheme } from "../game/simulation/customTrackTheme";
import { DraftHistory } from "./draftHistory";
import { writePreviewTransaction } from "./previewTransaction";
import {
  clearLocalDraftIfMatches,
  EDITOR_LOCAL_DRAFT_STORAGE_KEY,
  type FetchLike,
  readLocalDraft,
  ServerTracksClient,
  ServerTracksClientError,
  type ServerTrackEntry,
  type ServerTrackMetrics,
  type StorageLike,
  writeLocalDraft,
} from "./serverTracksClient";

export const EDITOR_MIN_POINTS = 8;
export const EDITOR_MAX_POINTS = 96;
export const EDITOR_MAX_FEATURES = 6;
export const EDITOR_MAX_ITEMS = 12;
const MAX_COORDINATE = 6_000;
const MAX_IMPORT_BYTES = 64 * 1024;
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 5;
const PREVIEW_BUTTON_COPY = "OPEN PREVIEW";

export interface CustomTrackEditorOptions {
  readonly serverClient?: Pick<ServerTracksClient, "capability" | "list" | "create" | "update" | "delete">;
  readonly storage?: StorageLike | null;
}

interface SavedTrackIdentity {
  readonly key: ServerTrackEntry["key"];
  readonly revision: number;
  readonly def: CustomTrackDefinition;
}

interface ServerUiState {
  capability: { enabled: boolean; authenticated: boolean; authorized: boolean } | null;
  tracks: ServerTrackEntry[];
  selectedKey: ServerTrackEntry["key"] | null;
  listReady: boolean;
  busy: boolean;
  action: string | null;
}

export type EditorTool = "move" | "insert" | "delete";

export interface EditorPointHit {
  readonly index: number;
  readonly distanceSq: number;
}

export interface EditorSegmentHit {
  /** Segment starts at `index` and ends at `(index + 1) % raw.length`. */
  readonly index: number;
  readonly distanceSq: number;
  readonly t: number;
  readonly point: CustomTrackPoint;
}

/** The first draft is intentionally valid before the editor has any input. */
export function createDefaultEditorDraft(): CustomTrackDefinition {
  const radius = 420;
  const raw: CustomTrackPoint[] = Array.from({ length: 16 }, (_, index) => {
    const angle = index / 16 * Math.PI * 2;
    return [Math.round(Math.cos(angle) * radius), Math.round(Math.sin(angle) * radius)];
  });
  return normalizeCustomTrackDefinition({
    name: "Untitled Circuit",
    raw,
    width: 84,
    themeKey: "bartop",
    enabled: true,
  });
}

export function clampEditorPoint(point: readonly [number, number]): CustomTrackPoint {
  const x = Number.isFinite(point[0]) ? Math.max(-MAX_COORDINATE, Math.min(MAX_COORDINATE, point[0])) : 0;
  const y = Number.isFinite(point[1]) ? Math.max(-MAX_COORDINATE, Math.min(MAX_COORDINATE, point[1])) : 0;
  return [x, y];
}

/** Return a detached point list with one control point moved. */
export function moveControlPoint(raw: readonly CustomTrackPoint[], index: number, point: readonly [number, number]): CustomTrackPoint[] {
  if (index < 0 || index >= raw.length) return raw.map(([x, y]) => [x, y]);
  const next = raw.map(([x, y]) => [x, y] as CustomTrackPoint);
  next[index] = clampEditorPoint(point);
  return next;
}

/** Insert after a segment start. Null means the 96-point guard was reached. */
export function insertControlPoint(raw: readonly CustomTrackPoint[], segmentIndex: number, point: readonly [number, number]): CustomTrackPoint[] | null {
  if (raw.length >= EDITOR_MAX_POINTS || raw.length < EDITOR_MIN_POINTS) return null;
  if (segmentIndex < 0 || segmentIndex >= raw.length) return null;
  const next = raw.map(([x, y]) => [x, y] as CustomTrackPoint);
  next.splice(segmentIndex + 1, 0, clampEditorPoint(point));
  return next;
}

/** Delete one control point. Null means the 8-point safety guard was reached. */
export function deleteControlPoint(raw: readonly CustomTrackPoint[], index: number): CustomTrackPoint[] | null {
  if (raw.length <= EDITOR_MIN_POINTS || index < 0 || index >= raw.length) return null;
  return raw.filter((_, candidate) => candidate !== index).map(([x, y]) => [x, y] as CustomTrackPoint);
}

export function nearestControlPoint(raw: readonly CustomTrackPoint[], target: readonly [number, number], maxDistance = Infinity): EditorPointHit | null {
  const limitSq = maxDistance * maxDistance;
  let best: EditorPointHit | null = null;
  for (let index = 0; index < raw.length; index += 1) {
    const dx = raw[index][0] - target[0];
    const dy = raw[index][1] - target[1];
    const distanceSq = dx * dx + dy * dy;
    if (distanceSq <= limitSq && (best === null || distanceSq < best.distanceSq)) best = { index, distanceSq };
  }
  return best;
}

export function nearestRouteSegment(raw: readonly CustomTrackPoint[], target: readonly [number, number]): EditorSegmentHit | null {
  if (raw.length < 2) return null;
  let best: EditorSegmentHit | null = null;
  for (let index = 0; index < raw.length; index += 1) {
    const a = raw[index];
    const b = raw[(index + 1) % raw.length];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const lengthSq = dx * dx + dy * dy;
    const t = lengthSq > 0
      ? Math.max(0, Math.min(1, ((target[0] - a[0]) * dx + (target[1] - a[1]) * dy) / lengthSq))
      : 0;
    const point: CustomTrackPoint = [a[0] + dx * t, a[1] + dy * t];
    const distanceSq = (target[0] - point[0]) ** 2 + (target[1] - point[1]) ** 2;
    if (best === null || distanceSq < best.distanceSq) best = { index, distanceSq, t, point };
  }
  return best;
}

function finiteOrThrow(value: string, label: string): number {
  if (!value.trim()) throw new Error(`${label} is required`);
  const result = Number(value);
  if (!Number.isFinite(result)) throw new Error(`${label} must be a finite number`);
  return result;
}

function titleCase(value: string): string {
  return value.replace(/(^|[-_])([a-z])/g, (_, prefix: string, letter: string) => `${prefix}${letter.toUpperCase()}`).replace(/-/g, " ");
}

function formatProgress(value: number): string {
  return value.toFixed(3);
}

function wrapProgress(value: number): number {
  return ((value % 1) + 1) % 1;
}

function pointAtProgress(raw: readonly CustomTrackPoint[], progress: number): CustomTrackPoint {
  const scaled = wrapProgress(progress) * raw.length;
  const index = Math.floor(scaled);
  const t = scaled - index;
  const a = raw[index % raw.length];
  const b = raw[(index + 1) % raw.length];
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

function colorHex(value: number): string {
  return `#${Math.max(0, Math.min(0xffffff, Math.floor(value))).toString(16).padStart(6, "0")}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function normalizeImportedEditorPayload(value: unknown): CustomTrackDefinition {
  if (isRecord(value) && Object.prototype.hasOwnProperty.call(value, "def")) {
    if (value.key === CUSTOM_TRACK_PREVIEW_KEY) return normalizeCustomTrackPreviewDocument(value).def;
    return normalizeCustomTrackSavedDocument(value).def;
  }
  return normalizeCustomTrackDefinition(value);
}

function downloadKey(name: string): `c_${string}` {
  const suffix = name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 15) || "draft";
  return `c_${suffix}`;
}

function definitionFingerprint(value: unknown): string {
  return JSON.stringify(normalizeCustomTrackDefinition(value));
}

function savedTrackIdentity(entry: ServerTrackEntry): SavedTrackIdentity {
  return { key: entry.key, revision: entry.revision, def: normalizeCustomTrackDefinition(entry.def) };
}

function metricSummary(metrics: ServerTrackMetrics | undefined): string {
  if (!metrics) return "";
  const preferred = ["offTrackPercent", "maxLateral", "samples", "durationMs"];
  const parts: string[] = [];
  for (const key of preferred) {
    const value = metrics[key];
    if (typeof value !== "number" && typeof value !== "string") continue;
    const formatted = typeof value === "number" ? Number.isInteger(value) ? String(value) : value.toFixed(2) : value;
    parts.push(`${key} ${formatted}`);
  }
  return parts.join(" · ");
}

export function createEditorSavedDocument(input: unknown) {
  const def = normalizeCustomTrackDefinition(input);
  return normalizeCustomTrackSavedDocument({ schemaVersion: CUSTOM_TRACK_SCHEMA_VERSION, key: downloadKey(def.name), def });
}

interface EditorView {
  centerX: number;
  centerY: number;
  scale: number;
  panX: number;
  panY: number;
  fitted: boolean;
}

interface DragInteraction {
  pointerId: number;
  index: number;
  startRaw: CustomTrackPoint[];
}

interface PanInteraction {
  pointerId: number;
  startX: number;
  startY: number;
  startPanX: number;
  startPanY: number;
}

interface PendingPreview {
  readonly revision: number;
  readonly url: string;
}

type ProgressFeature = "jumps" | "items" | "dips" | "tunnels";

const FEATURE_LIMITS: Readonly<Record<ProgressFeature, number>> = {
  jumps: EDITOR_MAX_FEATURES,
  items: EDITOR_MAX_ITEMS,
  dips: EDITOR_MAX_FEATURES,
  tunnels: EDITOR_MAX_FEATURES,
};

/** DOM controller for the standalone local course workshop. */
export class CustomTrackEditor {
  private history: DraftHistory;
  private readonly canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D;
  private readonly root: HTMLElement;
  private readonly statusLine: HTMLElement;
  private readonly validationSummary: HTMLElement;
  private readonly trackName: HTMLInputElement;
  private readonly widthInput: HTMLInputElement;
  private readonly widthValue: HTMLOutputElement;
  private readonly themeSelect: HTMLSelectElement;
  private readonly floorSelect: HTMLSelectElement;
  private readonly propsSelect: HTMLSelectElement;
  private readonly enabledInput: HTMLInputElement;
  private readonly zoomValue: HTMLOutputElement;
  private readonly previewButton: HTMLButtonElement;
  private readonly saveButton: HTMLButtonElement;
  private readonly trackKeyInput: HTMLInputElement;
  private readonly savedTrackSelect: HTMLSelectElement;
  private readonly refreshTracksButton: HTMLButtonElement;
  private readonly loadTrackButton: HTMLButtonElement;
  private readonly deleteTrackButton: HTMLButtonElement;
  private readonly serverStatus: HTMLElement;
  private readonly serverAccessBadge: HTMLElement;
  private readonly storage: StorageLike | null;
  private readonly serverClient: Pick<ServerTracksClient, "capability" | "list" | "create" | "update" | "delete">;
  private readonly view: EditorView = { centerX: 0, centerY: 0, scale: 1, panX: 0, panY: 0, fitted: false };
  private tool: EditorTool = "move";
  private selectedIndex = -1;
  private previewRaw: CustomTrackPoint[] | null = null;
  private drag: DragInteraction | null = null;
  private pan: PanInteraction | null = null;
  private pendingPreview: PendingPreview | null = null;
  private layout: CustomTrackLayout | null = null;
  private draftKey: string;
  private savedDraftKey: string;
  private savedIdentity: SavedTrackIdentity | null = null;
  private savedFingerprint: string;
  private localDraftWarningShown = false;
  private localDraftRecovered = false;
  private server: ServerUiState = { capability: null, tracks: [], selectedKey: null, listReady: false, busy: false, action: null };
  private canvasWidth = 0;
  private canvasHeight = 0;
  private pixelRatio = 1;

  constructor(root: HTMLElement, options: CustomTrackEditorOptions = {}) {
    this.root = root;
    const defaultDraft = createDefaultEditorDraft();
    this.storage = options.storage === undefined
      ? (() => { try { return window.localStorage; } catch { return null; } })()
      : options.storage;
    this.serverClient = options.serverClient ?? new ServerTracksClient();
    const localDraft = this.storage ? readLocalDraft(this.storage) : { status: "unavailable" as const, message: "Local draft storage is unavailable" };
    const initialDraft = localDraft.status === "valid" && localDraft.definition ? localDraft.definition : defaultDraft;
    this.history = new DraftHistory(initialDraft);
    this.draftKey = localDraft.status === "valid" && localDraft.key ? localDraft.key : downloadKey(initialDraft.name);
    this.savedDraftKey = localDraft.status === "valid" ? downloadKey(defaultDraft.name) : this.draftKey;
    this.savedFingerprint = definitionFingerprint(defaultDraft);
    this.localDraftRecovered = localDraft.status === "valid" && localDraft.definition !== undefined;
    if (localDraft.status === "invalid" || localDraft.status === "unavailable") this.localDraftWarningShown = true;
    this.canvas = this.require<HTMLCanvasElement>("track-canvas");
    const context = this.canvas.getContext("2d");
    if (!context) throw new Error("Track editor requires a 2D canvas");
    this.context = context;
    this.statusLine = this.require<HTMLElement>("status-line");
    this.validationSummary = this.require<HTMLElement>("validation-summary");
    this.trackName = this.require<HTMLInputElement>("track-name");
    this.widthInput = this.require<HTMLInputElement>("track-width");
    this.widthValue = this.require<HTMLOutputElement>("track-width-value");
    this.themeSelect = this.require<HTMLSelectElement>("theme-key");
    this.floorSelect = this.require<HTMLSelectElement>("floor-key");
    this.propsSelect = this.require<HTMLSelectElement>("props-key");
    this.enabledInput = this.require<HTMLInputElement>("enabled");
    this.zoomValue = this.require<HTMLOutputElement>("zoom-value");
    this.previewButton = this.require<HTMLButtonElement>("preview-button");
    this.saveButton = this.require<HTMLButtonElement>("save-button");
    this.trackKeyInput = this.require<HTMLInputElement>("track-key");
    this.savedTrackSelect = this.require<HTMLSelectElement>("saved-track-select");
    this.refreshTracksButton = this.require<HTMLButtonElement>("refresh-tracks-button");
    this.loadTrackButton = this.require<HTMLButtonElement>("load-track-button");
    this.deleteTrackButton = this.require<HTMLButtonElement>("delete-track-button");
    this.serverStatus = this.require<HTMLElement>("server-status");
    this.serverAccessBadge = this.require<HTMLElement>("server-access-badge");
    this.populateThemeSelects();
    this.bindEvents();
    this.resizeCanvas();
    this.fitRoute(false);
    this.render();
    this.updatePreviewAvailability();
    this.updateServerControls();
    if (this.localDraftRecovered) this.announce("Recovered a local draft. Review it before publishing.", "success");
    else if (this.localDraftWarningShown) this.announce("Local draft recovery is unavailable; the current draft remains in memory.", "warning");
    void this.initializeServer();
  }

  get document(): CustomTrackDefinition { return this.history.document; }
  get revision(): number { return this.history.revision; }
  get currentTool(): EditorTool { return this.tool; }

  private require<T extends HTMLElement>(id: string): T {
    const element = document.getElementById(id);
    if (!element) throw new Error(`Missing editor element #${id}`);
    return element as T;
  }

  private populateThemeSelects(): void {
    const selected = this.themeSelect.value;
    this.themeSelect.replaceChildren();
    for (const theme of CUSTOM_TRACK_THEME_KEYS) this.themeSelect.add(new Option(titleCase(theme), theme));
    this.themeSelect.value = selected || "bartop";
    for (const select of [this.floorSelect, this.propsSelect]) {
      for (const theme of CUSTOM_TRACK_THEME_KEYS) select.add(new Option(titleCase(theme), theme));
    }
  }

  private bindEvents(): void {
    this.require<HTMLButtonElement>("undo-button").addEventListener("click", () => this.undo());
    this.require<HTMLButtonElement>("redo-button").addEventListener("click", () => this.redo());
    this.require<HTMLButtonElement>("export-button").addEventListener("click", () => this.exportDocument());
    this.require<HTMLButtonElement>("import-button").addEventListener("click", () => this.require<HTMLInputElement>("import-file").click());
    this.require<HTMLInputElement>("import-file").addEventListener("change", (event) => { void this.importDocument(event); });
    this.saveButton.addEventListener("click", () => { void this.saveToServer(); });
    this.refreshTracksButton.addEventListener("click", () => { void this.refreshServerTracks(true); });
    this.savedTrackSelect.addEventListener("change", () => {
      const value = this.savedTrackSelect.value;
      this.server.selectedKey = value ? value as ServerTrackEntry["key"] : null;
      this.updateServerControls();
    });
    this.loadTrackButton.addEventListener("click", () => this.loadSelectedServerTrack());
    this.deleteTrackButton.addEventListener("click", () => { void this.deleteSelectedServerTrack(); });
    this.trackKeyInput.addEventListener("input", () => {
      this.draftKey = this.trackKeyInput.value.trim();
      this.persistLocalDraft();
      this.updateServerControls();
    });
    this.trackKeyInput.addEventListener("change", () => this.validateDraftKey(false));
    this.require<HTMLButtonElement>("zoom-out-button").addEventListener("click", () => this.zoomBy(.82));
    this.require<HTMLButtonElement>("zoom-in-button").addEventListener("click", () => this.zoomBy(1.22));
    this.require<HTMLButtonElement>("fit-button").addEventListener("click", () => this.fitRoute(true));
    for (const button of this.root.querySelectorAll<HTMLButtonElement>("[data-tool]")) {
      button.addEventListener("click", () => this.setTool(button.dataset.tool as EditorTool));
    }
    for (const [id, tool] of [["tool-move", "move"], ["tool-insert", "insert"], ["tool-delete", "delete"]] as const) {
      this.require<HTMLButtonElement>(id).addEventListener("click", () => this.setTool(tool));
    }
    this.trackName.addEventListener("change", () => this.commitForm("Course name updated"));
    this.themeSelect.addEventListener("change", () => this.commitForm("Theme updated"));
    this.floorSelect.addEventListener("change", () => this.commitForm("Floor pattern updated"));
    this.propsSelect.addEventListener("change", () => this.commitForm("Props set updated"));
    this.enabledInput.addEventListener("change", () => this.commitForm(this.enabledInput.checked ? "Course enabled" : "Course kept private"));
    this.widthInput.addEventListener("input", () => { this.widthValue.value = `${this.widthInput.value} units`; });
    this.widthInput.addEventListener("change", () => this.commitForm("Road width updated"));
    for (const input of this.root.querySelectorAll<HTMLInputElement>("[data-tint-enabled], [data-tint-input]")) {
      input.addEventListener("change", () => this.commitForm("Road tint updated"));
    }
    this.root.addEventListener("change", (event) => {
      const target = event.target;
      if (target instanceof HTMLInputElement && (target.matches("[data-feature-input]") || target.matches("[data-tunnel-input]"))) {
        this.commitForm("Feature progress updated");
      }
    });
    this.root.addEventListener("click", (event) => this.onDelegatedClick(event));
    this.canvas.addEventListener("pointerdown", (event) => this.onPointerDown(event));
    this.canvas.addEventListener("pointermove", (event) => this.onPointerMove(event));
    this.canvas.addEventListener("pointerup", (event) => this.onPointerUp(event, true));
    this.canvas.addEventListener("pointercancel", (event) => this.onPointerCancel(event));
    this.canvas.addEventListener("lostpointercapture", (event) => this.onPointerCancel(event));
    this.canvas.addEventListener("contextmenu", (event) => event.preventDefault());
    this.canvas.addEventListener("wheel", (event) => {
      event.preventDefault();
      this.zoomAt(event.deltaY < 0 ? 1.12 : .89, event.clientX, event.clientY);
    }, { passive: false });
    this.root.addEventListener("keydown", (event) => this.onKeyDown(event));
    window.addEventListener("resize", () => this.resizeCanvas());
    window.addEventListener("blur", () => this.cancelPointerInteraction());
    window.addEventListener("beforeunload", () => this.persistLocalDraft());
    if (typeof ResizeObserver !== "undefined") new ResizeObserver(() => this.resizeCanvas()).observe(this.canvas);
  }

  private onDelegatedClick(event: Event): void {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const add = target.closest<HTMLElement>("[data-add-feature]");
    if (add) {
      this.addFeature(add.dataset.addFeature as ProgressFeature);
      return;
    }
    const remove = target.closest<HTMLElement>("[data-remove-feature]");
    if (remove) {
      this.removeFeature(remove.dataset.removeFeature as ProgressFeature, Number(remove.dataset.index));
      return;
    }
    const pointDelete = target.closest<HTMLButtonElement>("[data-delete-selected]");
    if (pointDelete) this.deleteSelectedPoint();
  }

  private onKeyDown(event: KeyboardEvent): void {
    const target = event.target;
    const typing = target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement;
    if (!typing && (event.key === "Delete" || event.key === "Backspace")) {
      event.preventDefault();
      this.deleteSelectedPoint();
      return;
    }
    if (event.key === "Escape") {
      this.cancelPointerInteraction();
      return;
    }
    if (typing) return;
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z") {
      event.preventDefault();
      if (event.shiftKey) this.redo(); else this.undo();
    } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "y") {
      event.preventDefault();
      this.redo();
    }
  }

  private setTool(tool: EditorTool): void {
    this.tool = tool;
    for (const [id, candidate] of [["tool-move", "move"], ["tool-insert", "insert"], ["tool-delete", "delete"]] as const) {
      const button = this.require<HTMLButtonElement>(id);
      const active = candidate === tool;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", String(active));
    }
    this.canvas.style.cursor = tool === "move" ? "grab" : tool === "insert" ? "copy" : "not-allowed";
    this.announce(tool === "move" ? "Move points by dragging." : tool === "insert" ? "Click a road segment to insert a point." : "Click a point to remove it.", "warning");
    this.renderCanvas();
  }

  private currentRaw(): readonly CustomTrackPoint[] {
    return this.previewRaw ?? this.history.document.raw;
  }

  private formDefinition(): Record<string, unknown> {
    const current = this.history.document;
    const next: Record<string, unknown> = { ...current, raw: current.raw.map(([x, y]) => [x, y]) };
    next.name = this.trackName.value.trim();
    next.width = finiteOrThrow(this.widthInput.value, "Road width");
    next.themeKey = this.themeSelect.value;
    if (this.floorSelect.value) next.floorKey = this.floorSelect.value; else delete next.floorKey;
    if (this.propsSelect.value) next.propsKey = this.propsSelect.value; else delete next.propsKey;
    next.enabled = this.enabledInput.checked;
    for (const feature of ["jumps", "items", "dips"] as const) {
      const inputs = [...this.root.querySelectorAll<HTMLInputElement>(`[data-feature-input="${feature}"]`)];
      if (inputs.length || Object.prototype.hasOwnProperty.call(current, feature)) next[feature] = inputs.map((input) => finiteOrThrow(input.value, `${feature} progress`));
      else delete next[feature];
    }
    const tunnelInputs = [...this.root.querySelectorAll<HTMLInputElement>("[data-tunnel-input]")];
    const tunnels = [] as Array<{ u0: number; u1: number }>;
    for (let index = 0; index < tunnelInputs.length; index += 2) {
      tunnels.push({
        u0: finiteOrThrow(tunnelInputs[index]?.value ?? "", "Tunnel start"),
        u1: finiteOrThrow(tunnelInputs[index + 1]?.value ?? "", "Tunnel end"),
      });
    }
    if (tunnelInputs.length || Object.prototype.hasOwnProperty.call(current, "tunnels")) next.tunnels = tunnels;
    else delete next.tunnels;
    const tint: Record<string, string> = {};
    for (const key of ["kerb", "line", "glow"] as const) {
      const enabled = this.require<HTMLInputElement>(`tint-${key}-enabled`).checked;
      if (enabled) tint[key] = this.require<HTMLInputElement>(`tint-${key}`).value;
    }
    if (Object.keys(tint).length) next.tint = tint; else delete next.tint;
    return next;
  }

  private commitForm(message: string): boolean {
    try {
      return this.commitDraft(this.formDefinition(), message);
    } catch (error) {
      this.showValidation(error instanceof Error ? error.message : "Invalid course values");
      return false;
    }
  }

  private commitDraft(candidate: unknown, message: string): boolean {
    try {
      const changed = this.history.commit(candidate);
      if (!changed) {
        this.announce("No changes to record.", "warning");
        return false;
      }
      this.pendingPreview = null;
      this.selectedIndex = Math.min(this.selectedIndex, this.history.document.raw.length - 1);
      this.layout = null;
      this.clearValidation();
      this.persistLocalDraft();
      this.render();
      this.announce(`${message} · revision ${this.history.revision}`, "success");
      return true;
    } catch (error) {
      this.showValidation(error instanceof Error ? error.message : "Invalid course definition");
      return false;
    }
  }

  private serverReady(): boolean {
    const capability = this.server.capability;
    return Boolean(capability?.enabled && capability.authenticated && capability.authorized && this.server.listReady);
  }

  private draftIsDirty(): boolean {
    try { return definitionFingerprint(this.history.document) !== this.savedFingerprint || this.draftKey !== this.savedDraftKey; }
    catch { return true; }
  }

  private persistLocalDraft(): void {
    if (!this.storage) return;
    let key: string | undefined;
    try { normalizeCustomTrackId(this.draftKey); key = this.draftKey; } catch { /* Keep the definition recoverable even while the key is being edited. */ }
    if (writeLocalDraft(this.storage, this.history.document, key)) return;
    // Storage is best effort. Do not replace a user's draft with a warning on
    // every pointer move when private browsing or quota rules reject writes.
    this.localDraftWarningShown = true;
  }

  private validateDraftKey(announce = true): boolean {
    try {
      const key = normalizeCustomTrackId(this.draftKey);
      this.draftKey = key;
      this.trackKeyInput.value = key;
      this.trackKeyInput.removeAttribute("aria-invalid");
      this.trackKeyInput.setCustomValidity("");
      this.updateServerControls();
      return true;
    } catch {
      this.trackKeyInput.setAttribute("aria-invalid", "true");
      this.trackKeyInput.setCustomValidity("Use a lowercase c_ identifier.");
      if (announce) this.showValidation("Track key must start with c_ and use lowercase letters, numbers or underscores.");
      this.updateServerControls();
      return false;
    }
  }

  private setServerStatus(message: string, state: "ready" | "warning" | "error" | "checking" = "warning"): void {
    this.serverStatus.textContent = message;
    this.serverStatus.dataset.state = state;
  }

  private renderServerTrackList(): void {
    const selected = this.server.selectedKey;
    this.savedTrackSelect.replaceChildren(new Option("— choose a saved track —", ""));
    for (const entry of this.server.tracks) {
      const option = new Option(`${entry.def.name} · ${entry.key} · r${entry.revision}`, entry.key);
      this.savedTrackSelect.add(option);
    }
    this.savedTrackSelect.value = selected && this.server.tracks.some(entry => entry.key === selected) ? selected : "";
    this.server.selectedKey = this.savedTrackSelect.value ? this.savedTrackSelect.value as ServerTrackEntry["key"] : null;
  }

  private updateServerControls(): void {
    const ready = this.serverReady();
    const capabilityReady = Boolean(this.server.capability?.enabled && this.server.capability.authenticated && this.server.capability.authorized);
    const selected = this.server.selectedKey && this.server.tracks.some(entry => entry.key === this.server.selectedKey);
    const currentKey = this.draftKey;
    const updating = Boolean(this.savedIdentity && this.savedIdentity.key === currentKey);
    const saveLabel = this.server.busy ? "WORKING…" : updating ? "SAVE CHANGES" : "SAVE NEW TRACK";
    const saveHint = this.server.busy ? "BUSY" : !ready ? "UNAVAILABLE" : updating ? `REVISION ${this.savedIdentity?.revision}` : "CREATE";
    const label = this.saveButton.querySelector("span");
    if (this.saveButton.childNodes[0]) this.saveButton.childNodes[0].textContent = saveLabel;
    if (label) label.textContent = saveHint;
    this.saveButton.disabled = !ready || this.server.busy;
    this.refreshTracksButton.disabled = !capabilityReady || this.server.busy;
    this.savedTrackSelect.disabled = !ready || this.server.busy || this.server.tracks.length === 0;
    this.loadTrackButton.disabled = !ready || this.server.busy || !selected;
    this.deleteTrackButton.disabled = !ready || this.server.busy || !selected;
    this.trackKeyInput.value = document.activeElement === this.trackKeyInput ? this.trackKeyInput.value : this.draftKey;
    const capability = this.server.capability;
    const badge = !capability ? "CHECKING" : !capability.enabled ? "SERVER OFF" : !capability.authenticated ? "SIGN IN" : !capability.authorized ? "OPERATOR ONLY" : "OPERATOR READY";
    this.serverAccessBadge.textContent = badge;
    this.serverAccessBadge.dataset.state = ready ? "ready" : capability?.authenticated === false ? "auth" : "off";
  }

  private handleServerError(error: unknown, fallback = "Server access is unavailable; local editing remains available."): void {
    if (!(error instanceof ServerTracksClientError)) {
      this.setServerStatus(fallback, "error");
      this.announce(fallback, "warning");
      return;
    }
    if (error.kind === "unauthorized") {
      this.server.listReady = false;
      this.setServerStatus("Sign in to manage saved tracks.", "warning");
      this.announce("Sign in to use the saved-track library.", "warning");
    } else if (error.kind === "forbidden") {
      this.server.listReady = false;
      this.setServerStatus("This account is not configured as an operator.", "warning");
      this.announce("Operator access is required for saved tracks.", "warning");
    } else if (error.kind === "busy") {
      this.setServerStatus("The server is checking another layout. Try again shortly.", "warning");
      this.announce("Server admission is busy — nothing was changed.", "warning");
    } else if (error.kind === "admission-denied") {
      const metrics = metricSummary(error.metrics);
      const message = `Layout rejected: ${error.message}${metrics ? ` · ${metrics}` : ""}`;
      this.validationSummary.textContent = message;
      this.validationSummary.hidden = false;
      this.setServerStatus("The server rejected this layout; your draft is still open.", "error");
      this.announce("Track was not saved — review the admission details.", "error");
    } else if (error.kind === "conflict") {
      const current = error.current ? ` Server revision ${error.current.revision} is current.` : " The saved track changed or no longer exists.";
      const message = `Save conflict.${current} Your draft was kept; load the server copy only if you choose to.`;
      this.validationSummary.textContent = message;
      this.validationSummary.hidden = false;
      this.setServerStatus("Save conflict; no server copy replaced your draft.", "error");
      this.announce("Save conflict — your draft is still open.", "error");
    } else {
      this.setServerStatus(error.message || fallback, "error");
      this.announce(fallback, "warning");
    }
  }

  private async initializeServer(): Promise<void> {
    this.server.busy = true;
    this.server.action = "Checking server access";
    this.setServerStatus("Checking server access…", "checking");
    this.updateServerControls();
    try {
      const capability = await this.serverClient.capability();
      this.server.capability = capability;
      this.server.listReady = false;
      this.server.busy = false;
      this.server.action = null;
      this.updateServerControls();
      if (!capability.enabled) {
        this.setServerStatus("Server publishing is not configured; local export and preview remain available.", "warning");
        return;
      }
      if (!capability.authenticated) {
        this.setServerStatus("Sign in to inspect saved tracks.", "warning");
        return;
      }
      if (!capability.authorized) {
        this.setServerStatus("This account is not configured as an operator.", "warning");
        return;
      }
      await this.refreshServerTracks(false);
    } catch (error) {
      this.server.capability = null;
      this.server.listReady = false;
      this.handleServerError(error);
    } finally {
      this.server.busy = false;
      this.server.action = null;
      this.updateServerControls();
    }
  }

  private async refreshServerTracks(announce: boolean): Promise<boolean> {
    const capabilityReady = Boolean(this.server.capability?.enabled && this.server.capability.authenticated && this.server.capability.authorized);
    if (!capabilityReady || this.server.busy) return false;
    this.server.busy = true;
    this.server.action = "Loading saved tracks";
    this.setServerStatus("Loading saved tracks…", "checking");
    this.updateServerControls();
    try {
      this.server.tracks = [...await this.serverClient.list()].sort((left, right) => left.key.localeCompare(right.key));
      this.server.listReady = true;
      this.renderServerTrackList();
      this.setServerStatus(this.server.tracks.length ? `${this.server.tracks.length} saved track${this.server.tracks.length === 1 ? "" : "s"} available.` : "No saved tracks yet.", "ready");
      if (announce) this.announce("Saved-track list refreshed.", "success");
      return true;
    } catch (error) {
      this.server.listReady = false;
      this.handleServerError(error, "Saved-track list is unavailable; local editing remains available.");
      return false;
    } finally {
      this.server.busy = false;
      this.server.action = null;
      this.updateServerControls();
    }
  }

  private selectedServerTrack(): ServerTrackEntry | null {
    if (!this.server.selectedKey) return null;
    return this.server.tracks.find(entry => entry.key === this.server.selectedKey) ?? null;
  }

  private loadSelectedServerTrack(): void {
    if (!this.serverReady() || this.server.busy) return;
    const entry = this.selectedServerTrack();
    if (!entry) {
      this.announce("Choose a saved track first.", "warning");
      return;
    }
    if (this.draftIsDirty() && !window.confirm(`Load ${entry.def.name} and discard the current draft changes?`)) {
      this.announce("Load cancelled; your draft is still open.", "warning");
      return;
    }
    this.history = new DraftHistory(entry.def);
    this.savedIdentity = savedTrackIdentity(entry);
    this.savedFingerprint = definitionFingerprint(entry.def);
    this.draftKey = entry.key;
    this.savedDraftKey = entry.key;
    this.pendingPreview = null;
    this.selectedIndex = -1;
    this.layout = null;
    this.view.fitted = false;
    if (this.storage) clearLocalDraftIfMatches(this.storage, entry.def, entry.key);
    this.clearValidation();
    this.fitRoute(false);
    this.render();
    this.announce(`Loaded ${entry.def.name} · revision ${entry.revision}`, "success");
  }

  private syncFormBeforeServerAction(): boolean {
    try {
      const candidate = this.formDefinition();
      if (definitionFingerprint(candidate) !== definitionFingerprint(this.history.document)) this.commitDraft(candidate, "Draft fields updated");
      return true;
    } catch (error) {
      this.showValidation(error instanceof Error ? error.message : "Invalid course values");
      return false;
    }
  }

  private async saveToServer(): Promise<void> {
    if (!this.serverReady() || this.server.busy) return;
    if (!this.syncFormBeforeServerAction()) return;
    this.draftKey = this.trackKeyInput.value.trim();
    if (!this.validateDraftKey()) return;
    const draft = normalizeCustomTrackDefinition(this.history.document);
    const key = this.draftKey as ServerTrackEntry["key"];
    const identity = this.savedIdentity?.key === key ? this.savedIdentity : null;
    const startRevision = this.history.revision;
    const startFingerprint = definitionFingerprint(draft);
    const startKey = key;
    this.server.busy = true;
    this.server.action = identity ? "Saving changes" : "Creating track";
    this.setServerStatus(identity ? `Saving ${key}…` : `Creating ${key}…`, "checking");
    this.updateServerControls();
    try {
      const result = identity
        ? await this.serverClient.update(key, draft, identity.revision)
        : await this.serverClient.create(key, draft);
      this.savedIdentity = savedTrackIdentity(result.track);
      this.savedFingerprint = startFingerprint;
      this.savedDraftKey = startKey;
      this.server.tracks = [...this.server.tracks.filter(entry => entry.key !== result.track.key), result.track].sort((left, right) => left.key.localeCompare(right.key));
      this.server.selectedKey = result.track.key;
      this.renderServerTrackList();
      let formStillMatches = false;
      try { formStillMatches = definitionFingerprint(this.formDefinition()) === startFingerprint; } catch { /* Keep an invalid newer form visible for correction. */ }
      const currentStillMatches = this.history.revision === startRevision
        && formStillMatches
        && this.draftKey === startKey;
      if (currentStillMatches) {
        if (this.storage) clearLocalDraftIfMatches(this.storage, draft, startKey);
        this.render();
        const metrics = metricSummary(result.metrics);
        this.setServerStatus(`Saved ${result.track.def.name} at revision ${result.track.revision}.`, "ready");
        this.announce(`Saved ${result.track.key} · revision ${result.track.revision}${metrics ? ` · ${metrics}` : ""}`, "success");
      } else {
        // The server identity advances, but the newer in-memory edit remains
        // the user's draft and is intentionally not marked as saved.
        this.persistLocalDraft();
        this.updateServerControls();
        this.setServerStatus(`Saved revision ${result.track.revision}; newer draft edits remain unsaved.`, "warning");
        this.announce("Saved the earlier snapshot; newer draft edits remain open.", "warning");
      }
    } catch (error) {
      this.handleServerError(error, "Save failed; your draft remains open.");
    } finally {
      this.server.busy = false;
      this.server.action = null;
      this.updateServerControls();
    }
  }

  private async deleteSelectedServerTrack(): Promise<void> {
    if (!this.serverReady() || this.server.busy) return;
    const entry = this.selectedServerTrack();
    if (!entry) {
      this.announce("Choose a saved track first.", "warning");
      return;
    }
    if (!window.confirm(`Delete ${entry.def.name} (${entry.key})? This cannot be undone.`)) {
      this.announce("Delete cancelled.", "warning");
      return;
    }
    const key = entry.key;
    const revision = entry.revision;
    this.server.busy = true;
    this.server.action = "Deleting track";
    this.setServerStatus(`Deleting ${key}…`, "checking");
    this.updateServerControls();
    try {
      await this.serverClient.delete(key, revision);
      this.server.tracks = this.server.tracks.filter(candidate => candidate.key !== key);
      this.server.selectedKey = null;
      if (this.savedIdentity?.key === key) {
        this.savedIdentity = null;
        this.savedFingerprint = "";
        this.savedDraftKey = "";
      }
      this.renderServerTrackList();
      this.persistLocalDraft();
      this.setServerStatus(`Deleted ${key}.`, "ready");
      this.announce(`Deleted ${key}; the current draft was kept.`, "success");
    } catch (error) {
      this.handleServerError(error, "Delete failed; no track was removed.");
    } finally {
      this.server.busy = false;
      this.server.action = null;
      this.updateServerControls();
    }
  }

  private addFeature(feature: ProgressFeature): void {
    const current = this.history.document;
    const count = feature === "tunnels" ? current.tunnels?.length ?? 0 : (current[feature]?.length ?? 0);
    if (count >= FEATURE_LIMITS[feature]) {
      this.announce(`${titleCase(feature)} are limited to ${FEATURE_LIMITS[feature]}.`, "warning");
      return;
    }
    const next: Record<string, unknown> = { ...current };
    if (feature === "tunnels") {
      next.tunnels = [...(current.tunnels ?? []), { u0: 0.2, u1: 0.3 }];
    } else {
      const defaults: Record<Exclude<ProgressFeature, "tunnels">, number> = { jumps: 0.5, items: 0.75, dips: 0.25 };
      next[feature] = [...(current[feature] ?? []), defaults[feature]];
    }
    this.commitDraft(next, `${titleCase(feature)} feature added`);
  }

  private removeFeature(feature: ProgressFeature, index: number): void {
    if (!Number.isInteger(index) || index < 0) return;
    const current = this.history.document;
    const values = feature === "tunnels" ? current.tunnels ?? [] : current[feature] ?? [];
    if (index >= values.length) return;
    const next: Record<string, unknown> = { ...current };
    next[feature] = values.filter((_, candidate) => candidate !== index);
    this.commitDraft(next, `${titleCase(feature)} feature removed`);
  }

  private deleteSelectedPoint(): void {
    const index = this.selectedIndex;
    if (index < 0) {
      this.announce("Select a control point first.", "warning");
      return;
    }
    const nextRaw = deleteControlPoint(this.history.document.raw, index);
    if (!nextRaw) {
      this.announce(`A route needs at least ${EDITOR_MIN_POINTS} control points.`, "warning");
      return;
    }
    if (this.commitDraft({ ...this.history.document, raw: nextRaw }, `Point ${index + 1} deleted`)) {
      this.selectedIndex = Math.min(index, nextRaw.length - 1);
      this.renderCanvas();
    }
  }

  private undo(): void {
    if (!this.history.undo()) {
      this.announce("Nothing to undo.", "warning");
      return;
    }
    this.pendingPreview = null;
    this.selectedIndex = -1;
    this.layout = null;
    this.clearValidation();
    this.persistLocalDraft();
    this.render();
    this.announce(`Undo complete · revision ${this.history.revision}`, "success");
  }

  private redo(): void {
    if (!this.history.redo()) {
      this.announce("Nothing to redo.", "warning");
      return;
    }
    this.pendingPreview = null;
    this.selectedIndex = -1;
    this.layout = null;
    this.clearValidation();
    this.persistLocalDraft();
    this.render();
    this.announce(`Redo complete · revision ${this.history.revision}`, "success");
  }

  private showValidation(message: string): void {
    this.validationSummary.textContent = message;
    this.validationSummary.hidden = false;
    this.announce("Draft not changed — fix the highlighted value.", "error");
  }

  private clearValidation(): void {
    this.validationSummary.hidden = true;
    this.validationSummary.textContent = "";
  }

  private announce(message: string, state: "success" | "warning" | "error" = "warning"): void {
    this.statusLine.textContent = message;
    this.statusLine.dataset.state = state;
  }

  private render(): void {
    const current = this.history.document;
    this.trackName.value = current.name;
    if (document.activeElement !== this.trackKeyInput) this.trackKeyInput.value = this.draftKey;
    this.widthInput.value = String(current.width);
    this.widthValue.value = `${current.width} units`;
    this.themeSelect.value = current.themeKey;
    this.floorSelect.value = current.floorKey ?? "";
    this.propsSelect.value = current.propsKey ?? "";
    this.enabledInput.checked = current.enabled;
    this.renderTint(current);
    this.renderFeatureRows(current);
    this.renderStats(current);
    this.renderHistoryButtons();
    this.renderCanvas();
    this.updateServerControls();
  }

  private renderTint(current: CustomTrackDefinition): void {
    for (const key of ["kerb", "line", "glow"] as const) {
      const enabled = this.require<HTMLInputElement>(`tint-${key}-enabled`);
      const input = this.require<HTMLInputElement>(`tint-${key}`);
      const value = current.tint?.[key];
      enabled.checked = value !== undefined;
      if (value !== undefined) input.value = value;
      input.disabled = !enabled.checked;
    }
  }

  private renderFeatureRows(current: CustomTrackDefinition): void {
    const rows = (feature: ProgressFeature) => this.require<HTMLElement>(`${feature}-rows`);
    const values = (feature: Exclude<ProgressFeature, "tunnels">): readonly number[] => current[feature] ?? [];
    for (const feature of ["jumps", "items", "dips"] as const) {
      const entries = values(feature);
      rows(feature).innerHTML = entries.length
        ? entries.map((value, index) => `<div class="feature-row"><label for="${feature}-${index}">U${index + 1}<input id="${feature}-${index}" data-feature-input="${feature}" data-index="${index}" type="number" min="0" max="${feature === "jumps" ? ".999" : "1"}" step=".001" value="${formatProgress(value)}" /></label><button class="remove-feature-button" data-remove-feature="${feature}" data-index="${index}" type="button" aria-label="Remove ${feature} ${index + 1}">×</button></div>`).join("")
        : `<span class="feature-empty">No ${feature} placed yet.</span>`;
    }
    const tunnels = current.tunnels ?? [];
    rows("tunnels").innerHTML = tunnels.length
      ? tunnels.map((tunnel, index) => `<div class="feature-row tunnel-row"><label for="tunnel-${index}-start">FROM<input id="tunnel-${index}-start" data-tunnel-input="start" data-index="${index}" type="number" min="0" max="1" step=".001" value="${formatProgress(tunnel.u0)}" /></label><label for="tunnel-${index}-end">TO<input id="tunnel-${index}-end" data-tunnel-input="end" data-index="${index}" type="number" min="0" max="1" step=".001" value="${formatProgress(tunnel.u1)}" /></label><button class="remove-feature-button" data-remove-feature="tunnels" data-index="${index}" type="button" aria-label="Remove tunnel ${index + 1}">×</button></div>`).join("")
      : "<span class=\"feature-empty\">No tunnels placed yet.</span>";
    for (const feature of ["jumps", "items", "dips", "tunnels"] as const) {
      const count = feature === "tunnels" ? tunnels.length : values(feature).length;
      const add = this.root.querySelector<HTMLButtonElement>(`[data-add-feature="${feature}"]`);
      if (add) add.disabled = count >= FEATURE_LIMITS[feature];
    }
  }

  private renderStats(current: CustomTrackDefinition): void {
    this.require<HTMLElement>("point-count").textContent = `${current.raw.length} / ${EDITOR_MAX_POINTS}`;
    const layout = this.layout ?? this.tryBuildLayout(current);
    this.layout = layout;
    this.require<HTMLElement>("route-length").textContent = layout ? `${layout.length.toFixed(0)} m` : "INVALID";
    this.require<HTMLElement>("bridge-count").textContent = layout ? String(layout.bridges.length) : "—";
    this.require<HTMLElement>("revision-count").textContent = String(this.history.revision);
    const badge = this.require<HTMLElement>("draft-badge");
    badge.textContent = current.enabled ? "ENABLED" : "LOCAL DRAFT";
    badge.dataset.state = current.enabled ? "enabled" : "draft";
    if (!layout) {
      this.validationSummary.textContent = "The route cannot be prepared yet. Spread the control points into a non-degenerate loop.";
      this.validationSummary.hidden = false;
    }
  }

  private tryBuildLayout(current: CustomTrackDefinition): CustomTrackLayout | null {
    try { return buildCustomTrackLayout(current); } catch { return null; }
  }

  private renderHistoryButtons(): void {
    this.require<HTMLButtonElement>("undo-button").disabled = !this.history.canUndo;
    this.require<HTMLButtonElement>("redo-button").disabled = !this.history.canRedo;
  }

  private updatePreviewAvailability(): void {
    let available = false;
    try {
      const storage = window.localStorage;
      const probe = `beerkart-editor-probe-${Date.now()}`;
      storage.setItem(probe, "1");
      storage.removeItem(probe);
      available = typeof window.crypto?.randomUUID === "function";
    } catch { available = false; }
    this.previewButton.disabled = !available;
    const hint = this.previewButton.querySelector("span");
    if (hint) hint.textContent = available ? "LOCAL HANDOFF" : "BROWSER STORAGE REQUIRED";
    if (available) {
      this.previewButton.title = "Open this draft in the 3D editor preview";
      this.previewButton.addEventListener("click", () => this.openPreview());
    } else {
      this.previewButton.title = "Preview needs browser storage and crypto.randomUUID";
    }
  }

  private openPreview(): void {
    const current = this.history.document;
    let url = this.pendingPreview?.revision === this.history.revision ? this.pendingPreview.url : null;
    try {
      if (!url) {
        const token = window.crypto.randomUUID();
        url = writePreviewTransaction(window.localStorage, token, this.history.revision, current);
        this.pendingPreview = { revision: this.history.revision, url };
      }
    } catch (error) {
      this.showValidation(error instanceof Error ? error.message : "Preview storage is unavailable");
      return;
    }
    // Open a same-origin blank handle first so browsers that return null for a
    // `noopener` navigation do not make a successful preview look blocked.
    // Detach the opener before navigating the handle to the short-lived URL.
    const opened = window.open("", "_blank");
    if (!opened) {
      this.announce("Preview popup blocked — allow pop-ups, then retry.", "warning");
      return;
    }
    try {
      opened.opener = null;
      opened.location.href = new URL(url, window.location.href).href;
    } catch {
      this.announce("Preview could not be navigated — retry the local preview.", "warning");
      return;
    }
    this.pendingPreview = null;
    this.announce("Preview opened in a new tab. The draft stays local until the runtime accepts it.", "success");
  }

  private exportDocument(): void {
    try {
      const current = normalizeCustomTrackDefinition(this.history.document);
      const document = createEditorSavedDocument(current);
      const blob = new Blob([JSON.stringify(document, null, 2)], { type: "application/json" });
      const link = window.document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${downloadKey(current.name)}.json`;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(link.href), 0);
      this.announce(`Downloaded ${link.download}.`, "success");
    } catch (error) {
      this.showValidation(error instanceof Error ? error.message : "Export failed");
    }
  }

  private async importDocument(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    try {
      if (file.size > MAX_IMPORT_BYTES) throw new Error("Import file exceeds the 64 KiB editor limit");
      const imported = normalizeImportedEditorPayload(JSON.parse(await file.text()));
      this.history = new DraftHistory(imported);
      this.savedIdentity = null;
      this.savedFingerprint = definitionFingerprint(createDefaultEditorDraft());
      this.draftKey = downloadKey(this.history.document.name);
      this.savedDraftKey = downloadKey(createDefaultEditorDraft().name);
      this.pendingPreview = null;
      this.selectedIndex = -1;
      this.layout = null;
      this.view.fitted = false;
      this.clearValidation();
      this.fitRoute(false);
      this.persistLocalDraft();
      this.render();
      this.announce(`Imported ${file.name} · revision ${this.history.revision}`, "success");
    } catch (error) {
      this.showValidation(error instanceof Error ? error.message : "Import failed");
    }
  }

  private resizeCanvas(): void {
    const rect = this.canvas.getBoundingClientRect();
    const width = Math.max(320, Math.floor(rect.width || this.canvas.parentElement?.clientWidth || 800));
    const height = Math.max(260, Math.floor(rect.height || this.canvas.parentElement?.clientHeight || 600));
    this.pixelRatio = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
    this.canvasWidth = width;
    this.canvasHeight = height;
    const backingWidth = Math.floor(width * this.pixelRatio);
    const backingHeight = Math.floor(height * this.pixelRatio);
    if (this.canvas.width !== backingWidth || this.canvas.height !== backingHeight) {
      this.canvas.width = backingWidth;
      this.canvas.height = backingHeight;
      // A fitted desktop view can be far too large after a phone rotation or
      // responsive breakpoint. Recompute the viewport fit whenever its pixel
      // dimensions change; explicit zoom/pan controls remain available after.
      this.fitRoute(false);
    }
  }

  private fitRoute(announce: boolean): void {
    const raw = this.currentRaw();
    if (!raw.length) return;
    const prepared = this.previewRaw ? null : this.layout ?? this.tryBuildLayout(this.history.document);
    this.layout = prepared;
    const points: CustomTrackPoint[] = raw.map(([x, y]) => [x, y]);
    if (prepared) for (const sample of prepared.samples) points.push([sample.x / CUSTOM_TRACK_METRES_PER_UNIT, sample.z / CUSTOM_TRACK_METRES_PER_UNIT]);
    let minX = points[0][0], maxX = points[0][0], minY = points[0][1], maxY = points[0][1];
    for (const point of points) {
      minX = Math.min(minX, point[0]); maxX = Math.max(maxX, point[0]);
      minY = Math.min(minY, point[1]); maxY = Math.max(maxY, point[1]);
    }
    const spanX = Math.max(1, maxX - minX);
    const spanY = Math.max(1, maxY - minY);
    const margin = Math.max(120, Math.max(spanX, spanY) * .18);
    this.view.centerX = (minX + maxX) * .5;
    this.view.centerY = (minY + maxY) * .5;
    this.view.scale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.min((this.canvasWidth - 60) / (spanX + margin), (this.canvasHeight - 60) / (spanY + margin))));
    this.view.panX = 0;
    this.view.panY = 0;
    this.view.fitted = true;
    this.updateZoomLabel();
    this.renderCanvas();
    if (announce) this.announce("Route fitted to canvas.", "success");
  }

  private zoomBy(factor: number): void {
    this.view.scale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, this.view.scale * factor));
    this.updateZoomLabel();
    this.renderCanvas();
  }

  private zoomAt(factor: number, clientX: number, clientY: number): void {
    const before = this.screenToWorld(clientX, clientY);
    this.view.scale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, this.view.scale * factor));
    const rect = this.canvas.getBoundingClientRect();
    const sx = clientX - rect.left;
    const sy = clientY - rect.top;
    this.view.panX = sx - rect.width * .5 - (before[0] - this.view.centerX) * this.view.scale;
    this.view.panY = sy - rect.height * .5 - (before[1] - this.view.centerY) * this.view.scale;
    this.updateZoomLabel();
    this.renderCanvas();
  }

  private updateZoomLabel(): void {
    this.zoomValue.value = `${Math.round(this.view.scale * 100)}%`;
  }

  private screenToWorld(clientX: number, clientY: number): CustomTrackPoint {
    const rect = this.canvas.getBoundingClientRect();
    return [
      this.view.centerX + (clientX - rect.left - rect.width * .5 - this.view.panX) / this.view.scale,
      this.view.centerY + (clientY - rect.top - rect.height * .5 - this.view.panY) / this.view.scale,
    ];
  }

  private worldToScreen(point: readonly [number, number]): CustomTrackPoint {
    return [
      this.canvasWidth * .5 + this.view.panX + (point[0] - this.view.centerX) * this.view.scale,
      this.canvasHeight * .5 + this.view.panY + (point[1] - this.view.centerY) * this.view.scale,
    ];
  }

  private onPointerDown(event: PointerEvent): void {
    event.preventDefault();
    this.canvas.focus();
    if (event.button === 1 || event.button === 2 || event.shiftKey) {
      this.pan = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, startPanX: this.view.panX, startPanY: this.view.panY };
      this.canvas.setPointerCapture(event.pointerId);
      return;
    }
    const world = this.screenToWorld(event.clientX, event.clientY);
    const raw = this.history.document.raw;
    if (this.tool === "move") {
      const hit = nearestControlPoint(raw, world, 24 / this.view.scale);
      if (!hit) {
        this.announce("Select a control point or Shift-drag to pan.", "warning");
        return;
      }
      this.selectedIndex = hit.index;
      this.drag = { pointerId: event.pointerId, index: hit.index, startRaw: raw.map(([x, y]) => [x, y]) };
      this.canvas.setPointerCapture(event.pointerId);
      this.renderCanvas();
      return;
    }
    if (this.tool === "delete") {
      const hit = nearestControlPoint(raw, world, 24 / this.view.scale);
      if (!hit) {
        this.announce("Click a control point to delete it.", "warning");
        return;
      }
      this.selectedIndex = hit.index;
      this.deleteSelectedPoint();
      return;
    }
    const segment = nearestRouteSegment(raw, world);
    if (!segment || segment.distanceSq > (32 / this.view.scale) ** 2) {
      this.announce("Click closer to the road to insert a point.", "warning");
      return;
    }
    const nextRaw = insertControlPoint(raw, segment.index, segment.point);
    if (!nextRaw) {
      this.announce(`A route can have at most ${EDITOR_MAX_POINTS} control points.`, "warning");
      return;
    }
    this.selectedIndex = segment.index + 1;
    this.commitDraft({ ...this.history.document, raw: nextRaw }, `Point ${this.selectedIndex + 1} inserted`);
  }

  private onPointerMove(event: PointerEvent): void {
    if (this.pan?.pointerId === event.pointerId) {
      this.view.panX = this.pan.startPanX + event.clientX - this.pan.startX;
      this.view.panY = this.pan.startPanY + event.clientY - this.pan.startY;
      this.renderCanvas();
      return;
    }
    if (this.drag?.pointerId !== event.pointerId) return;
    const point = this.screenToWorld(event.clientX, event.clientY);
    const rounded: CustomTrackPoint = [Math.round(point[0] * 10) / 10, Math.round(point[1] * 10) / 10];
    this.previewRaw = moveControlPoint(this.drag.startRaw, this.drag.index, rounded);
    this.renderCanvas();
  }

  private onPointerUp(event: PointerEvent, commit: boolean): void {
    if (this.pan?.pointerId === event.pointerId) {
      this.pan = null;
      this.releasePointer(event.pointerId);
      return;
    }
    if (this.drag?.pointerId !== event.pointerId) return;
    const preview = this.previewRaw;
    this.drag = null;
    this.previewRaw = null;
    this.releasePointer(event.pointerId);
    if (commit && preview && JSON.stringify(preview) !== JSON.stringify(this.history.document.raw)) {
      this.commitDraft({ ...this.history.document, raw: preview }, `Point ${this.selectedIndex + 1} moved`);
    } else {
      this.renderCanvas();
    }
  }

  private onPointerCancel(event: PointerEvent | Event): void {
    if (event instanceof PointerEvent) this.releasePointer(event.pointerId);
    if (this.drag || this.pan) this.announce("Pointer edit cancelled.", "warning");
    this.drag = null;
    this.pan = null;
    this.previewRaw = null;
    this.renderCanvas();
  }

  private cancelPointerInteraction(): void {
    if (!this.drag && !this.pan) return;
    this.drag = null;
    this.pan = null;
    this.previewRaw = null;
    this.renderCanvas();
    this.announce("Pointer edit cancelled.", "warning");
  }

  private releasePointer(pointerId: number): void {
    try { if (this.canvas.hasPointerCapture(pointerId)) this.canvas.releasePointerCapture(pointerId); } catch { /* pointer already cancelled */ }
  }

  private drawPolyline(raw: readonly CustomTrackPoint[], close: boolean): void {
    const [first] = raw;
    if (!first) return;
    this.context.beginPath();
    this.context.moveTo(first[0], first[1]);
    for (let index = 1; index < raw.length; index += 1) this.context.lineTo(raw[index][0], raw[index][1]);
    if (close) this.context.closePath();
  }

  private drawProgressSegment(raw: readonly CustomTrackPoint[], u0: number, u1: number): void {
    const start = wrapProgress(u0);
    let end = wrapProgress(u1);
    if (end <= start) end += 1;
    const count = Math.max(2, Math.ceil((end - start) * raw.length * 2));
    const first = pointAtProgress(raw, start);
    this.context.beginPath();
    this.context.moveTo(first[0], first[1]);
    for (let index = 1; index <= count; index += 1) {
      const point = pointAtProgress(raw, start + (end - start) * index / count);
      this.context.lineTo(point[0], point[1]);
    }
    this.context.stroke();
  }

  private drawMarker(point: CustomTrackPoint, fill: string, shape: "circle" | "diamond" | "ring", label?: string): void {
    const radius = Math.max(5 / this.view.scale, 3);
    this.context.save();
    this.context.fillStyle = fill;
    this.context.strokeStyle = "rgba(5, 14, 10, .95)";
    this.context.lineWidth = Math.max(1 / this.view.scale, .8);
    this.context.beginPath();
    if (shape === "diamond") {
      this.context.moveTo(point[0], point[1] - radius);
      this.context.lineTo(point[0] + radius, point[1]);
      this.context.lineTo(point[0], point[1] + radius);
      this.context.lineTo(point[0] - radius, point[1]);
      this.context.closePath();
    } else {
      this.context.arc(point[0], point[1], radius, 0, Math.PI * 2);
    }
    if (shape === "ring") {
      this.context.strokeStyle = fill;
      this.context.lineWidth = Math.max(2.5 / this.view.scale, 1.3);
      this.context.stroke();
    } else {
      this.context.fill();
      this.context.stroke();
    }
    if (label) {
      this.context.font = `${Math.max(9 / this.view.scale, 4)}px Inter, sans-serif`;
      this.context.fillStyle = "#fff8e8";
      this.context.textAlign = "center";
      this.context.fillText(label, point[0], point[1] - radius - 4 / this.view.scale);
    }
    this.context.restore();
  }

  private renderCanvas(): void {
    if (!this.canvasWidth || !this.canvasHeight) return;
    const current = this.history.document;
    const raw = this.currentRaw();
    const prepared = this.previewRaw ? null : this.layout;
    const route = prepared
      ? prepared.samples.slice(0, -1).map((sample) => [sample.x / CUSTOM_TRACK_METRES_PER_UNIT, sample.z / CUSTOM_TRACK_METRES_PER_UNIT] as CustomTrackPoint)
      : raw;
    const theme = getCustomTrackTheme(current);
    const context = this.context;
    context.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);
    context.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
    context.fillStyle = colorHex(theme.ground);
    context.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
    context.save();
    context.translate(this.canvasWidth * .5 + this.view.panX, this.canvasHeight * .5 + this.view.panY);
    context.scale(this.view.scale, this.view.scale);
    context.translate(-this.view.centerX, -this.view.centerY);
    const left = this.view.centerX - (this.canvasWidth * .5 + this.view.panX) / this.view.scale;
    const right = this.view.centerX + (this.canvasWidth * .5 - this.view.panX) / this.view.scale;
    const top = this.view.centerY - (this.canvasHeight * .5 + this.view.panY) / this.view.scale;
    const bottom = this.view.centerY + (this.canvasHeight * .5 - this.view.panY) / this.view.scale;
    const span = Math.max(right - left, bottom - top);
    const exponent = Math.floor(Math.log10(Math.max(1, span / 7)));
    const base = 10 ** exponent;
    const step = span / (base * 7) < 2 ? base : span / (base * 7) < 5 ? base * 2 : base * 5;
    context.strokeStyle = "rgba(220, 246, 222, .1)";
    context.lineWidth = 1 / this.view.scale;
    for (let x = Math.floor(left / step) * step; x <= right; x += step) { context.beginPath(); context.moveTo(x, top); context.lineTo(x, bottom); context.stroke(); }
    for (let y = Math.floor(top / step) * step; y <= bottom; y += step) { context.beginPath(); context.moveTo(left, y); context.lineTo(right, y); context.stroke(); }
    context.strokeStyle = "rgba(243, 197, 82, .15)";
    context.lineWidth = 2 / this.view.scale;
    context.beginPath(); context.moveTo(left, 0); context.lineTo(right, 0); context.stroke();
    context.beginPath(); context.moveTo(0, top); context.lineTo(0, bottom); context.stroke();
    this.drawPolyline(route, true);
    context.lineJoin = "round";
    context.lineCap = "round";
    context.strokeStyle = "rgba(6, 13, 9, .88)";
    context.lineWidth = Math.max(2, current.width * 2);
    context.stroke();
    this.drawPolyline(route, true);
    context.strokeStyle = colorHex(theme.road);
    context.lineWidth = Math.max(1, current.width * 2 - 10);
    context.stroke();
    this.drawPolyline(route, true);
    context.strokeStyle = "rgba(255, 248, 224, .36)";
    context.lineWidth = Math.max(1 / this.view.scale, 1.4);
    context.setLineDash([14 / this.view.scale, 12 / this.view.scale]);
    context.stroke();
    context.setLineDash([]);
    const first = route[0] ?? raw[0];
    const second = route[1] ?? first;
    const tx = second[0] - first[0], ty = second[1] - first[1];
    const tangentLength = Math.hypot(tx, ty) || 1;
    context.strokeStyle = "#fff8e8";
    context.lineWidth = 4 / this.view.scale;
    context.beginPath();
    context.moveTo(first[0] - ty / tangentLength * current.width, first[1] + tx / tangentLength * current.width);
    context.lineTo(first[0] + ty / tangentLength * current.width, first[1] - tx / tangentLength * current.width);
    context.stroke();
    const preparedMarker = (progress: number): CustomTrackPoint => {
      if (!prepared) return pointAtProgress(raw, progress);
      const count = prepared.samples.length - 1;
      const index = ((Math.round(progress * count) % count) + count) % count;
      const sample = prepared.samples[index];
      return [sample.x / CUSTOM_TRACK_METRES_PER_UNIT, sample.z / CUSTOM_TRACK_METRES_PER_UNIT];
    };
    for (const progress of current.jumps ?? []) {
      const point = preparedMarker(progress);
      this.drawMarker(point, "#f3c552", "circle", "J");
    }
    const itemProgress = prepared?.itemProgress ?? current.items ?? [];
    for (const progress of itemProgress) this.drawMarker(preparedMarker(progress), "#74e6dc", "diamond", "I");
    for (const progress of current.dips ?? []) this.drawMarker(preparedMarker(progress), "#b68166", "ring", "D");
    for (const tunnel of current.tunnels ?? []) {
      context.strokeStyle = "rgba(131, 201, 255, .38)";
      context.lineWidth = current.width * 1.75;
      this.drawProgressSegment(route, tunnel.u0, tunnel.u1);
    }
    if (prepared) {
      for (const bridge of prepared.bridges) {
        const over = prepared.samples[Math.min(prepared.samples.length - 1, bridge.overIndex)];
        if (over) this.drawMarker([over.x / CUSTOM_TRACK_METRES_PER_UNIT, over.z / CUSTOM_TRACK_METRES_PER_UNIT], "#ffe28d", "ring", "B");
      }
    }
    for (let index = 0; index < raw.length; index += 1) {
      const point = raw[index];
      const selected = index === this.selectedIndex;
      this.drawMarker(point, selected ? "#fff8e8" : "#f3c552", selected ? "ring" : "circle");
    }
    if (route[0]) this.drawMarker(route[0], "#74e6dc", "ring", "START");
    context.restore();
    this.updateZoomLabel();
  }
}

export function bootstrapEditor(root: HTMLElement | null = typeof document === "undefined" ? null : document.getElementById("editor-app")): CustomTrackEditor | null {
  if (!root) return null;
  return new CustomTrackEditor(root);
}

if (typeof document !== "undefined") bootstrapEditor();
